import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lock, Users, Download, Check, X as XIcon, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestsView() {
  const { slug } = useParams();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null); // { invitation, rsvps }
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  // Try owner-session first on mount (cookie), so the owner sees the list without typing a password
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.post(`${API}/invitations/slug/${slug}/guests/list`, {}, { withCredentials: true });
        setData(res.data);
      } catch {
        /* not owner — will show the password prompt */
      }
    })();
  }, [slug]);

  const submitPassword = async (e) => {
    e?.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/invitations/slug/${slug}/guests/list`, { password }, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Contraseña incorrecta";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await axios.post(
        `${API}/invitations/slug/${slug}/guests/export`,
        { password: password || null },
        { responseType: "blob", withCredentials: true }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invitados-${slug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Excel descargado");
    } catch {
      toast.error("No se pudo descargar el Excel");
    } finally {
      setExporting(false);
    }
  };

  // ----- Password prompt screen -----
  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream p-6">
        <form onSubmit={submitPassword} className="w-full max-w-sm text-center" data-testid="guests-password-form">
          <div className="w-16 h-16 mx-auto rounded-full bg-warmgold/10 grid place-items-center border border-warmgold/30">
            <Lock size={24} className="text-warmgold" />
          </div>
          <h1 className="headline text-3xl md:text-4xl text-ink mt-6">Lista de invitados</h1>
          <p className="text-inksoft text-sm mt-2">Este link es privado. Ingresa la contraseña que te compartió el anfitrión.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            className="mt-6 w-full bg-white border border-warmgold/40 rounded-full px-5 py-3 text-center focus:border-warmgold focus:outline-none text-ink"
            data-testid="guests-password-input"
            autoFocus
          />
          {error && <p className="text-red-500 text-xs mt-3" data-testid="guests-password-error">{error}</p>}
          <button
            type="submit"
            disabled={submitting || !password.trim()}
            className="btn-primary mt-5 w-full disabled:opacity-60"
            data-testid="guests-password-submit"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />} Ver lista
          </button>
          <Link to="/" className="btn-ghost mt-4 inline-flex" data-testid="guests-home-link">
            <ArrowLeft size={14} /> Ir al inicio
          </Link>
        </form>
      </div>
    );
  }

  // ----- Guest list screen -----
  const inv = data.invitation || {};
  const rsvps = data.rsvps || [];
  const totalYes = rsvps.filter((r) => r.will_attend).length;
  const totalNo = rsvps.filter((r) => !r.will_attend).length;
  const totalPeople = rsvps.filter((r) => r.will_attend).reduce((sum, r) => sum + (r.guests_count || 1), 0);
  const totalGirl = rsvps.filter((r) => r.prediction === "girl").length;
  const totalBoy = rsvps.filter((r) => r.prediction === "boy").length;
  const hasPredictions = totalGirl + totalBoy > 0;

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-4xl mx-auto px-5 py-8">
        <header className="mb-8" data-testid="guests-header">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="overline">Lista de invitados</p>
              <h1 className="headline text-3xl md:text-4xl text-ink mt-1">{inv.title || "Invitación"}</h1>
              {inv.hosts && <p className="script text-warmgold text-xl mt-1">{inv.hosts}</p>}
            </div>
            <button
              onClick={exportXlsx}
              disabled={exporting || rsvps.length === 0}
              className="btn-primary text-sm py-2.5 px-5 disabled:opacity-50"
              data-testid="guests-export-btn"
            >
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Descargar Excel
            </button>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Fecha" value={inv.event_date || "—"} />
            <StatCard label="Hora" value={inv.event_time || "—"} />
            <StatCard label="Lugar" value={inv.venue_name || "—"} />
            <StatCard label="Dirección" value={inv.address || "—"} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <StatCard label="Confirmados" value={totalYes} highlight />
            <StatCard label="Personas" value={totalPeople} highlight />
            <StatCard label="No asistirán" value={totalNo} />
          </div>
          {hasPredictions && (
            <div className="mt-3 grid grid-cols-2 gap-3" data-testid="guests-predictions">
              <StatCard label="Predicen niña 💗" value={totalGirl} tone="pink" />
              <StatCard label="Predicen niño 💙" value={totalBoy} tone="blue" />
            </div>
          )}
        </header>

        <div className="rounded-2xl border border-warmgold/25 bg-white overflow-hidden">
          <div className="hidden md:grid grid-cols-12 bg-warmgold/10 border-b border-warmgold/20 px-4 py-3 text-xs uppercase tracking-widest text-inksoft">
            <div className="col-span-1">#</div>
            <div className={hasPredictions ? "col-span-3" : "col-span-4"}>Nombre</div>
            <div className="col-span-2 text-center">Personas</div>
            <div className="col-span-2 text-center">Asistirá</div>
            {hasPredictions && <div className="col-span-2 text-center">Predicción</div>}
            <div className={hasPredictions ? "col-span-2" : "col-span-3"}>Mensaje</div>
          </div>
          {rsvps.length === 0 ? (
            <div className="p-10 text-center text-inksoft" data-testid="guests-empty">
              <Users size={28} className="mx-auto text-warmgold/60" />
              <p className="mt-3 text-sm">Aún no hay confirmaciones registradas.</p>
            </div>
          ) : (
            <ul>
              {rsvps.map((r, i) => (
                <li
                  key={`${r.name}-${i}`}
                  className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-warmgold/10 last:border-b-0"
                  data-testid={`guests-row-${i}`}
                >
                  <div className="col-span-1 text-inksoft text-sm">{i + 1}</div>
                  <div className={`col-span-11 ${hasPredictions ? "md:col-span-3" : "md:col-span-4"} text-ink font-medium`}>{r.name}</div>
                  <div className="col-span-4 md:col-span-2 md:text-center text-inksoft text-sm">
                    <span className="md:hidden text-[10px] uppercase tracking-widest mr-1">Personas:</span>
                    {r.guests_count || 1}
                  </div>
                  <div className="col-span-4 md:col-span-2 md:text-center">
                    {r.will_attend ? (
                      <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium"><Check size={12} /> Sí</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium"><XIcon size={12} /> No</span>
                    )}
                  </div>
                  {hasPredictions && (
                    <div className="col-span-4 md:col-span-2 md:text-center" data-testid={`guests-prediction-${i}`}>
                      {r.prediction === "girl" ? (
                        <span className="inline-flex items-center gap-1 text-pink-600 text-xs font-medium">💗 Niña</span>
                      ) : r.prediction === "boy" ? (
                        <span className="inline-flex items-center gap-1 text-sky-600 text-xs font-medium">💙 Niño</span>
                      ) : (
                        <span className="text-inksoft/50 text-xs">—</span>
                      )}
                    </div>
                  )}
                  <div className={`col-span-12 ${hasPredictions ? "md:col-span-2" : "md:col-span-3"} text-inksoft text-sm italic`}>{r.message || "—"}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight, tone }) {
  const toneClasses =
    tone === "pink"
      ? "border-pink-300 bg-pink-50"
      : tone === "blue"
      ? "border-sky-300 bg-sky-50"
      : highlight
      ? "border-warmgold/50 bg-warmgold/10"
      : "border-warmgold/20 bg-white";
  const valueColor =
    tone === "pink"
      ? "text-pink-600"
      : tone === "blue"
      ? "text-sky-600"
      : highlight
      ? "text-warmgold"
      : "text-ink";
  return (
    <div className={`rounded-2xl px-4 py-3 border ${toneClasses}`}>
      <p className="text-[10px] uppercase tracking-widest text-inksoft">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${valueColor}`}>{value}</p>
    </div>
  );
}
